<template>
    <div class="bg-slate-50 min-h-screen py-10">
        <div class="max-w-6xl mx-auto px-4 space-y-10">

            <!-- LOADER -->
            <div v-if="loadingGlobal" class="flex justify-center py-20">
                <div class="w-16 h-16 border-4 border-gray-200 border-t-orange-600 rounded-full animate-spin"></div>
            </div>

            <div v-if="!loadingGlobal && societe" class="space-y-8">

                <!-- HEADER -->
                <div class="mb-8">
                    <h1 class="text-2xl font-bold text-gray-900">Fiche société</h1>
                    <p class="text-sm text-gray-500 mt-1">Vue complète des informations de contact et des comptes associés</p>
                </div>

                <!-- INFOS SOCIETE -->
                <div class="bg-white rounded-2xl shadow-sm border p-8">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">

                        <div>
                            <div class="text-xs uppercase text-gray-400 font-semibold">Nom</div>
                            <div class="text-gray-900 font-medium text-lg mt-1">{{ societe.nom || '-' }}</div>
                        </div>

                        <div>
                            <div class="text-xs uppercase text-gray-400 font-semibold">SIRET</div>
                            <div class="text-gray-900 mt-1">{{ societe.siret || '-' }}</div>
                        </div>

                        <div>
                            <div class="text-xs uppercase text-gray-400 font-semibold">Numéro capacité</div>
                            <div class="text-gray-900 mt-1">{{ societe.num_capacite || '-' }}</div>
                        </div>

                        <div>
                            <div class="text-xs uppercase text-gray-400 font-semibold">Téléphone</div>
                            <div class="text-gray-900 mt-1">{{ societe.tel || '-' }}</div>
                        </div>

                        <div>
                            <div class="text-xs uppercase text-gray-400 font-semibold">Email</div>
                            <div class="text-gray-900 mt-1">{{ societe.mail || '-' }}</div>
                        </div>

                        <div>
                            <div class="text-xs uppercase text-gray-400 font-semibold">Google Avis</div>
                            <div class="mt-1">
                                <a v-if="societe.url_google_avis" :href="societe.url_google_avis" target="_blank" class="text-blue-600 hover:underline">
                                    Voir la page
                                </a>
                                <span v-else>-</span>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- RESPONSABLE -->
                <div class="bg-white rounded-2xl shadow-sm border p-8">
                    <h2 class="text-lg font-semibold text-gray-900 mb-6">Responsable</h2>

                    <div class="flex items-center justify-between">
                        <div class="text-gray-900 font-medium">
                            {{ societe.responsable_prenom || '-' }} {{ societe.responsable_nom || '' }}
                        </div>

                        <div v-if="societe.signature_entreprise">
                            <img :src="societe.signature_entreprise" class="h-16 object-contain">
                        </div>
                    </div>
                </div>

                <!-- ADRESSE -->
                <div class="bg-white rounded-2xl shadow-sm border p-8">
                    <h2 class="text-lg font-semibold text-gray-900 mb-6">Adresse</h2>

                    <div class="text-gray-800 leading-relaxed">
                        <div>{{ societe.adresse || '-' }}</div>
                        <div>{{ societe.code_postal || '' }} {{ societe.ville || '' }}</div>
                        <div>{{ societe.pays || '' }}</div>
                    </div>
                </div>

                <!-- HORAIRES -->
                <div class="bg-white rounded-2xl shadow-sm border p-8">
                    <h2 class="text-lg font-semibold text-gray-900 mb-6">Horaires</h2>

                    <div class="text-gray-800">
                        {{ societe.heure_ouverture || '--:--' }} — {{ societe.heure_fermeture || '--:--' }}
                    </div>
                </div>

                <!-- COMPTES ASSOCIES -->
                <div class="bg-white rounded-2xl shadow-sm border overflow-hidden">
                    <div class="px-8 py-6 border-b">
                        <h2 class="text-lg font-semibold text-gray-900">Comptes rattachés</h2>
                    </div>

                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                                <th class="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th class="px-8 py-3 text-left text-xs font-medium text-gray-500 uppercase">Droits</th>
                            </tr>
                        </thead>

                        <tbody class="divide-y divide-gray-200">

                            <tr v-if="!accounts.length">
                                <td colspan="3" class="px-8 py-6 text-center text-gray-500">
                                    Aucun compte rattaché
                                </td>
                            </tr>

                            <tr v-for="account in accounts" :key="account.id" class="hover:bg-gray-50">
                                <td class="px-8 py-4 font-medium text-gray-900">
                                    {{ account.prenom }} {{ account.nom }}
                                </td>

                                <td class="px-8 py-4 text-gray-600">
                                    {{ account.email }}
                                </td>

                                <td class="px-8 py-4 text-xs space-x-1">
                                    <span v-if="account.can_intervention" class="px-2 py-1 bg-blue-100 text-blue-700 rounded">Interv.</span>
                                    <span v-if="account.can_invoice" class="px-2 py-1 bg-red-100 text-red-700 rounded">Fact.</span>
                                    <span v-if="account.can_stats" class="px-2 py-1 bg-green-100 text-green-700 rounded">Stats</span>
                                    <span v-if="account.can_stock" class="px-2 py-1 bg-purple-100 text-purple-700 rounded">Stock</span>
                                    <span v-if="account.can_settings" class="px-2 py-1 bg-gray-200 text-gray-700 rounded">Settings</span>
                                </td>
                            </tr>

                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
export default {

    data() {
        return {
            societeId: this.$route.params.id,
            societe: {},
            accounts: [],
            loadingGlobal: false
        }
    },

    mounted() {
        this.loadData()
    },

    methods: {

        async loadData() {
            this.loadingGlobal = true

            let res = await this.$request.post('/admin/societes/view', {
                id: this.societeId
            })

            this.societe = res
            this.accounts = res.accounts

            this.loadingGlobal = false
        }
    }
}
</script>
